// Calculate reasonable quantity based on item type and event data
function calculateReasonableQuantity(item, category, eventData) {
  // If quantity is already set from theme, use it
  if (item.quantity && item.quantity > 1) {
    return item.quantity;
  }

  const guestCount = eventData?.guest_count || 0;
  const itemNameLower = (item.name || '').toLowerCase();
  const categoryLower = (category || '').toLowerCase();

  // Table-related items (centerpieces, tablecloths, etc.)
  if (itemNameLower.includes('centerpiece') || 
      itemNameLower.includes('tablecloth') || 
      itemNameLower.includes('table runner') ||
      itemNameLower.includes('table setting') ||
      (categoryLower === 'decor' && itemNameLower.includes('table'))) {
    // Estimate tables: typically 6-8 guests per table
    const guestsPerTable = 6;
    const numTables = Math.ceil(guestCount / guestsPerTable);
    return Math.max(1, numTables);
  }

  // Per-guest items (favors, place cards, etc.)
  if (itemNameLower.includes('favor') || 
      itemNameLower.includes('place card') ||
      itemNameLower.includes('name tag') ||
      itemNameLower.includes('welcome bag')) {
    return Math.max(1, guestCount);
  }

  // Food items - typically per person or per serving
  if (categoryLower.includes('food') || categoryLower.includes('cake')) {
    // Most food items are per person
    return Math.max(1, guestCount);
  }

  // Activities/games - typically one per event or per group
  if (categoryLower.includes('activit') || categoryLower.includes('game')) {
    // Usually 1-3 sets depending on guest count
    if (guestCount <= 20) return 1;
    if (guestCount <= 50) return 2;
    return 3;
  }

  // Balloons - typically need multiple for decoration
  if (itemNameLower.includes('balloon')) {
    // Estimate: ~1 balloon per 2-3 guests for basic decoration, more for elaborate setups
    if (guestCount <= 10) return Math.max(5, guestCount);
    if (guestCount <= 20) return Math.max(10, Math.ceil(guestCount * 0.8));
    if (guestCount <= 50) return Math.ceil(guestCount * 0.6);
    return Math.ceil(guestCount * 0.5); // For larger events, ~1 per 2 guests
  }

  // Lighting - typically 1-3 sets depending on venue size
  if (itemNameLower.includes('light') || itemNameLower.includes('lighting')) {
    if (guestCount <= 20) return 1;
    if (guestCount <= 50) return 2;
    return 3;
  }

  // Decor items - estimate based on venue size and item type
  if (categoryLower === 'decor') {
    // For general decor items, scale with guest count
    if (guestCount <= 10) return 1;
    if (guestCount <= 20) return 2;
    if (guestCount <= 50) return 3;
    if (guestCount <= 100) return 4;
    return Math.ceil(guestCount / 25); // ~1 per 25 guests for larger events
  }

  // Default: 1 item
  return 1;
}

export function initializeItemsFromTheme(theme, eventData = null) {
  const allItems = [];
  if (!theme?.categories) return allItems;

  Object.entries(theme.categories).forEach(([category, categoryItems]) => {
    if (!Array.isArray(categoryItems)) return;
    categoryItems.forEach((item, idx) => {
      // Use quantity from OpenAI if provided, otherwise calculate
      const quantity = item.quantity || calculateReasonableQuantity(item, category, eventData);
      
      // Use price_per_unit from OpenAI if provided, otherwise derive from buy_price
      let pricePerUnit = item.price_per_unit || null;
      let rentPricePerUnit = item.rent_price_per_unit || null;
      
      // If OpenAI provided buy_price but not price_per_unit, derive it
      // (This handles backward compatibility with old items)
      if (!pricePerUnit && item.buy_price) {
        const buyPriceNum = parseFloat(String(item.buy_price).replace(/[^0-9.]/g, ''));
        const qty = quantity || 1;
        if (!isNaN(buyPriceNum) && qty > 0) {
          pricePerUnit = `$${Math.round(buyPriceNum / qty * 100) / 100}`; // Round to 2 decimals
        }
      }
      
      // If OpenAI provided rent_price but not rent_price_per_unit, derive it
      if (!rentPricePerUnit && item.rent_price && item.rentable) {
        const rentPriceNum = parseFloat(String(item.rent_price).replace(/[^0-9.]/g, ''));
        const qty = quantity || 1;
        if (!isNaN(rentPriceNum) && qty > 0) {
          rentPricePerUnit = `$${Math.round(rentPriceNum / qty * 100) / 100}`;
        }
      }
      
      // Use buy_price from OpenAI (it should already be total = price_per_unit * quantity)
      // If not provided, calculate from price_per_unit and quantity
      let totalBuyPrice = item.buy_price || null;
      if (!totalBuyPrice && pricePerUnit && quantity) {
        const ppuNum = parseFloat(String(pricePerUnit).replace(/[^0-9.]/g, ''));
        if (!isNaN(ppuNum)) {
          totalBuyPrice = `$${Math.round(ppuNum * quantity)}`;
        }
      }
      
      // Use rent_price from OpenAI (it should already be total = rent_price_per_unit * quantity)
      // If not provided, calculate from rent_price_per_unit and quantity
      let totalRentPrice = item.rent_price || null;
      if (!totalRentPrice && rentPricePerUnit && quantity && item.rentable) {
        const rppuNum = parseFloat(String(rentPricePerUnit).replace(/[^0-9.]/g, ''));
        if (!isNaN(rppuNum)) {
          totalRentPrice = `$${Math.round(rppuNum * quantity)}`;
        }
      }
      
      allItems.push({
        id: `${category}-${idx}`,
        source_category: category,
        source_index: idx,
        category,
        name: item.name,
        // New separate fields for better image search
        item_name: item.item_name || item.name, // Core product name (e.g., "Banners")
        item_color: item.item_color || null, // Specific color name (e.g., "Sky Whisper")
        item_theme: item.item_theme || null, // Theme context (e.g., "Starlit Wilderness")
        description: item.description,
        buy_price: totalBuyPrice, // Total price (from OpenAI or calculated)
        rent_price: totalRentPrice, // Total rent price (from OpenAI or calculated)
        originalBuyPricePerUnit: pricePerUnit, // Store per-unit price for quantity adjustments
        originalRentPricePerUnit: rentPricePerUnit, // Store per-unit rent price
        rentable: item.rentable || false,
        food_type: item.food_type || null,
        meal_type: item.meal_type || null,
        color_tag: item.color_tag || null,
        priority: item.priority || 'nice-to-have',
        purchased: false,
        quantity: quantity, // From OpenAI or calculated
        quantityManuallyEdited: false, // Track if user manually edited quantity
        shortlistedLinks: [],
        // For organized games
        game_type: item.game_type || null,
        game_instructions: item.game_instructions || null,
        game_link: item.game_link || null
      });
    });
  });

  return allItems;
}
